import type { KeyboardEvent, MouseEvent } from 'react';

import { FromPriceDisplay } from '../PriceDisplay';
import {
  ActionButton,
  ActionButtonIcon,
  ActionButtonLabel,
  CardWrapper,
  EditSearchButton,
  Label,
  Name,
  PriceWrapper,
  RightSection,
  TextContent,
  Thumbnail,
  ThumbnailImage,
  ThumbnailPlaceholder,
  UnavailableBody,
  UnavailableDivider,
  UnavailableHeader,
  UnavailableText,
  UnavailableTextWrapper,
} from './ListItemCard.styles';
import { THUMBNAIL_IMAGE_SIZING } from '../EntityImage';
import { PlusIcon } from '../../icons/PlusIcon';
import type { ListItemCardProps } from './ListItemCard.types';

export function ListItemCard({
  name,
  label,
  image,
  hideImage,
  price,
  buttonLabel = 'Select',
  onSelect,
  unavailable,
  unavailableLabel = 'Unavailable',
  editSearchLabel = 'Edit search',
  onEditSearch,
}: ListItemCardProps) {
  const clickable = Boolean(onSelect) && !unavailable;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect?.();
    }
  };

  const handleActionClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onSelect?.();
  };

  const priceDisplay = price ? (
    <FromPriceDisplay
      amount={price.amount}
      currencyCode={price.currencyCode}
      label={price.label}
      discount={price.discount}
      locale={price.locale}
      size="base"
    />
  ) : null;

  const textContent = (
    <TextContent>
      <Name>{name}</Name>
      {label && <Label>{label}</Label>}
    </TextContent>
  );

  const thumbnail = !hideImage ? (
    <Thumbnail>
      {image?.src ? (
        <ThumbnailImage src={image.src} alt={image.alt ?? name} sizing={THUMBNAIL_IMAGE_SIZING} />
      ) : (
        <ThumbnailPlaceholder data-testid="thumbnail-placeholder" />
      )}
    </Thumbnail>
  ) : null;

  return (
    <CardWrapper
      $unavailable={unavailable}
      $clickable={clickable}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? name : undefined}
      onClick={clickable ? onSelect : undefined}
      onKeyDown={clickable ? handleKeyDown : undefined}
      data-testid="list-item-card"
    >
      {unavailable ? (
        <UnavailableBody>
          <UnavailableHeader>
            {thumbnail}
            {textContent}
          </UnavailableHeader>
          <UnavailableTextWrapper>
            <UnavailableText>{unavailableLabel}</UnavailableText>
            {onEditSearch && (
              <>
                <UnavailableDivider aria-hidden />
                <EditSearchButton type="button" onClick={onEditSearch}>
                  {editSearchLabel}
                </EditSearchButton>
              </>
            )}
          </UnavailableTextWrapper>
        </UnavailableBody>
      ) : (
        <>
          {thumbnail}
          {textContent}
          <RightSection>
            {priceDisplay && <PriceWrapper>{priceDisplay}</PriceWrapper>}
            {onSelect && (
              <ActionButton onClick={handleActionClick} aria-label={buttonLabel}>
                <ActionButtonLabel>{buttonLabel}</ActionButtonLabel>
                <ActionButtonIcon aria-hidden>
                  <PlusIcon size={13} />
                </ActionButtonIcon>
              </ActionButton>
            )}
          </RightSection>
        </>
      )}
    </CardWrapper>
  );
}
